// composables/useIdleDetection.js
import Vue from 'vue'
import ApiService from '@/core/services/api.service'

export function useIdleDetection(router) {
    const state = Vue.observable({
        isIdle: false,
        timeLeft: 0,
        idleTimeoutMinutes: 10,
        isActive: false
    })

    let idleTimer = null
    let countdownTimer = null
    let warningTimer = null

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

    function checkAuthStatus() {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token')
        return !!token
    }

    function cleanup() {
        clearTimeout(idleTimer)
        clearTimeout(warningTimer)
        clearInterval(countdownTimer)
        events.forEach(event => {
            document.removeEventListener(event, resetTimer, true)
        })
        state.isIdle = false
        state.timeLeft = 0
        state.isActive = false
    }

    function startIdleDetection() {
        if (!checkAuthStatus() || state.isActive) return
        state.isActive = true
        events.forEach(event => {
            document.addEventListener(event, resetTimer, true)
        })
        resetTimer()
    }

    function stopIdleDetection() {
        cleanup()
    }

    function resetTimer() {
        if (!checkAuthStatus() || !state.isActive) return

        clearTimeout(idleTimer)
        clearTimeout(warningTimer)
        clearInterval(countdownTimer)
        state.isIdle = false

        if (state.idleTimeoutMinutes > 1) {
            warningTimer = setTimeout(() => {
                showCountdown()
            }, (state.idleTimeoutMinutes - 1) * 60 * 1000)
        }

        idleTimer = setTimeout(() => {
            logout()
        }, state.idleTimeoutMinutes * 60 * 1000)
    }

    function showCountdown() {
        if (!checkAuthStatus()) return

        state.isIdle = true
        state.timeLeft = 60

        countdownTimer = setInterval(() => {
            state.timeLeft--
            if (state.timeLeft <= 0 || !checkAuthStatus()) {
                logout()
            }
        }, 1000)
    }

    async function logout() {
        if (!checkAuthStatus()) return
        cleanup()
        try {
            await ApiService.post('/auth/logout')
        } catch (error) {
            // Ignore API error, proceed to logout
        } finally {
            localStorage.clear()
            if (router.currentRoute.path !== '/login') {
                router.push('/login')
            }
        }
    }

    function stayLoggedIn() {
        if (!checkAuthStatus()) return
        resetTimer()
    }

    async function fetchIdleSettings() {
        if (!checkAuthStatus()) return
        try {
            const response = await ApiService.get('/user/idle-setting')
            state.idleTimeoutMinutes = response.data.idle_timeout_minutes || 10
        } catch (error) {
            state.idleTimeoutMinutes = 10
        }
    }

    async function updateIdleSettings(minutes) {
        if (!checkAuthStatus()) throw new Error('User not authenticated')
        try {
            await ApiService.put('/user/idle-setting', {
                idle_timeout_minutes: minutes
            })
            state.idleTimeoutMinutes = minutes
            if (state.isActive) {
                resetTimer()
            }
        } catch (error) {
            throw error
        }
    }

    async function initializeIdleDetection() {
        if (!checkAuthStatus()) return
        await fetchIdleSettings()
        startIdleDetection()
    }

    // Expose state and methods
    return {
        state,
        initializeIdleDetection,
        stopIdleDetection,
        stayLoggedIn,
        updateIdleSettings
    }
}